import { useState, useEffect } from "react";
import api, { apiErr } from "@/lib/api";
import { dateIt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/shared";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Link2 } from "lucide-react";

export function PlusButton({ onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      title="Aggiungi voce manuale"
      className="h-6 w-6 grid place-items-center rounded-md border border-primary/40 text-primary hover:bg-primary hover:text-white transition-colors shrink-0"
    >
      <Plus className="h-4 w-4" />
    </button>
  );
}

export function ManualItem({ entry, onEdit, onDelete, badge }) {
  return (
    <div
      className="flex items-start justify-between rounded-lg border border-dashed border-primary/40 bg-primary/5 px-3 py-2"
      data-testid={`manual-entry-${entry.id}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex rounded-full bg-primary/15 text-primary text-[10px] font-bold px-1.5 py-0.5">Manuale</span>
          {badge}
          <span className="text-sm font-medium text-slate-800 truncate">{entry.title}</span>
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
          {entry.date && <span>{dateIt(entry.date)}</span>}
          {entry.link_label && <span className="inline-flex items-center gap-1"><Link2 className="h-3 w-3" />{entry.link_label}</span>}
          {entry.notes && <span className="truncate max-w-[220px]">{entry.notes}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0 ml-2">
        <button onClick={() => onEdit(entry)} data-testid={`edit-manual-${entry.id}`} className="h-6 w-6 grid place-items-center rounded hover:bg-muted text-slate-500"><Pencil className="h-3.5 w-3.5" /></button>
        <button onClick={() => onDelete(entry)} data-testid={`delete-manual-${entry.id}`} className="h-6 w-6 grid place-items-center rounded hover:bg-rose-50 text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
}

export function EntryDialog({ state, onClose, onSaved }) {
  const { open, section, label, entry } = state;
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [linkType, setLinkType] = useState("none");
  const [linkId, setLinkId] = useState("");
  const [ops, setOps] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(entry?.title || "");
    setDate(entry?.date || "");
    setNotes(entry?.notes || "");
    setLinkType(entry?.link_type || "none");
    setLinkId(entry?.link_id || "");
    api.get("/operations").then((r) => setOps(r.data)).catch(() => {});
    api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
  }, [open, entry]);

  const save = async () => {
    setSaving(true);
    const payload = {
      section,
      title,
      date,
      notes,
      link_type: linkType === "none" ? null : linkType,
      link_id: linkType === "none" ? null : linkId || null,
    };
    try {
      if (entry) await api.put(`/dashboard-entries/${entry.id}`, payload);
      else await api.post("/dashboard-entries", payload);
      toast.success(entry ? "Voce aggiornata" : "Voce aggiunta");
      onClose();
      onSaved();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{entry ? "Modifica voce" : "Nuova voce"} · {label}</DialogTitle>
          <DialogDescription>Voce manuale del cruscotto. Non influisce su fatturato, conteggi finanziari o operazioni concluse.</DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-3">
          <Field label="Titolo *"><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="entry-title" placeholder="Es. Chiamare il notaio" /></Field>
          <Field label="Data (opzionale)"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="entry-date" /></Field>
          <Field label="Collega a (opzionale)">
            <Select value={linkType} onValueChange={(v) => { setLinkType(v); setLinkId(""); }}>
              <SelectTrigger data-testid="entry-link-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessun collegamento</SelectItem>
                <SelectItem value="operation">Operazione</SelectItem>
                <SelectItem value="contact">Contatto</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {linkType === "operation" && (
            <Field label="Operazione">
              <Select value={linkId} onValueChange={setLinkId}>
                <SelectTrigger data-testid="entry-link-op"><SelectValue placeholder="Seleziona operazione" /></SelectTrigger>
                <SelectContent>
                  {ops.map((o) => <SelectItem key={o.id} value={o.id}>{o.property_label || o.id}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          {linkType === "contact" && (
            <Field label="Contatto">
              <Select value={linkId} onValueChange={setLinkId}>
                <SelectTrigger data-testid="entry-link-contact"><SelectValue placeholder="Seleziona contatto" /></SelectTrigger>
                <SelectContent>
                  {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Note (opzionale)"><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="entry-notes" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving || !title.trim()} data-testid="entry-save">{saving ? "Salvataggio..." : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
